import React, { useMemo } from 'react';
import {
  GlobalStyles,
  ThemeProvider as MuiThemeProvider,
  Theme,
  StyledEngineProvider,
} from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import {
  deDE,
  enUS,
  esES,
  frFR,
  nlNL,
  bgBG,
  csCZ,
  huHU,
  itIT,
  plPL,
  ptPT,
  roRO,
  skSK,
  trTR,
  koKR,
  fiFI,
  jaJP,
  svSE,
  ukUA,
  ruRU,
  nnNO,
  srRS,
  hrHR,
  daDK,
  Localization,
} from '@mui/material/locale';

import { AppRouter } from './routers/app.router';

import { ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PlaceholderNavigation } from '@monorepo/react/material-default-layout';
import { AppConfigContext, defaultAppConfig } from './common/config';
import { useGetConfigQuery, useGetDefaultsQuery } from './app.generated';
import { locales } from '@monorepo/utils/date-fns-format';
import createMiraTheme from './theme/mira';
import { THEMES } from './theme/mira/constants';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const muiLocales: { [key: string]: Localization } = {
  de: deDE,
  en: enUS,
  es: esES,
  fr: frFR,
  fi: fiFI,
  ja: jaJP,
  nl: nlNL,
  bg: bgBG,
  cs: csCZ,
  hu: huHU,
  it: itIT,
  pl: plPL,
  pt: ptPT,
  ro: roRO,
  ru: ruRU,
  sk: skSK,
  sv: svSE,
  tr: trTR,
  ko: koKR,
  ua: ukUA,
  nn: nnNO,
  no: nnNO,
  hr: hrHR,
  sr: srRS,
  da: daDK,
};

export const App: React.FC = () => {
  const {
    i18n: { language },
  } = useTranslation(['translation', 'admin']);
  const [themeName, setThemeName] = React.useState<string>(() => {
    return localStorage.getItem('miraTheme') || THEMES.DEFAULT;
  });

  React.useEffect(() => {
    const handleThemeChange = () => {
      setThemeName(localStorage.getItem('miraTheme') || THEMES.DEFAULT);
    };

    window.addEventListener('miraThemeChange', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);

    return () => {
      window.removeEventListener('miraThemeChange', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, []);

  const { data } = useGetConfigQuery({
    fetchPolicy: 'no-cache',
  });
  const { data: defaultsData } = useGetDefaultsQuery({
    fetchPolicy: 'no-cache',
  });
  const config = React.useMemo(() => {
    if (!data?.config) return undefined;
    return {
      ...data.config,
      defaultSourceId: defaultsData?.sources[0]?.id,
    };
  }, [data, defaultsData]);
  const theme = useMemo(() => {
    const paletteOverrides = {
      primaryMain: (config || defaultAppConfig).colorSchemePrimary,
      secondaryMain: (config || defaultAppConfig).colorSchemeSecondary,
    };
    return createMiraTheme(
      themeName,
      paletteOverrides,
      muiLocales[language?.substring(0, 2).toLocaleLowerCase()] ?? enUS,
    );
  }, [language, config, themeName]);

  return (
    <StylesProvider injectFirst>
      <AppConfigContext.Provider value={config || defaultAppConfig}>
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={theme}>
            <GlobalStyles
              styles={(theme) => ({
                ':root': {
                  '--priority-high':
                    theme.palette.mode === 'dark' ? '#ff8a80' : '#a94442',
                  '--priority-normal':
                    theme.palette.mode === 'dark' ? '#e6e6e6' : '#212529',
                  '--priority-low':
                    theme.palette.mode === 'dark' ? '#a0a4a8' : '#6c757d',
                },
                /* Cover Dynamic Island / safe area with primary color */
                'html::before': {
                  content: '""',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 'env(safe-area-inset-top)',
                  backgroundColor: theme.palette.primary.main,
                  zIndex: 99999,
                  pointerEvents: 'none',
                },
              })}
            />
            <StylesThemeProvider theme={theme}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={
                    locales[language?.substring(0, 2).toLocaleLowerCase()] ||
                    locales.en
                  }
                >
                  <React.Suspense fallback={<PlaceholderNavigation />}>
                    <AppRouter />
                  </React.Suspense>
                </LocalizationProvider>
              </ThemeProvider>
            </StylesThemeProvider>
          </MuiThemeProvider>
        </StyledEngineProvider>
      </AppConfigContext.Provider>
    </StylesProvider>
  );
};
